import cn from 'classnames'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import { IColumn, IRowAction } from 'core-components/library/list/types'

import styles from './GroupedPaginatedTable.module.scss'

const GROUP_ROW_NAME_COLUMN_SPAN = 6

interface IGroupedRow {
  EntryId?: number
  isGroupRow?: boolean
  groupKey?: string
}

interface IGroupedPaginatedTableProps<T extends IGroupedRow> {
  rows: T[]
  columns: IColumn<T>[]
  pageSize?: number
  rowActions?: IRowAction<T>[]
  rowActionsVisible?: 'visible' | 'hidden'
  highlightedEntryIds?: ReadonlySet<number>
}

export const GroupedPaginatedTable = <T extends IGroupedRow>({
  rows,
  columns,
  pageSize = 5,
  rowActions = [],
  rowActionsVisible = 'hidden',
  highlightedEntryIds,
}: IGroupedPaginatedTableProps<T>) => {
  const [page, setPage] = useState(0)

  const pages = useMemo(() => {
    const result: T[][] = []

    let currentGroupRow: T | null = null
    let currentPage: T[] = []
    let currentPageItemsCount = 0

    rows.forEach((row) => {
      if (row.isGroupRow) {
        currentGroupRow = row
        return
      }

      if (currentPageItemsCount === pageSize) {
        result.push(currentPage)

        currentPage = currentGroupRow ? [currentGroupRow] : []
        currentPageItemsCount = 0
      }

      if (
        currentGroupRow &&
        !currentPage.some((item) => item.isGroupRow && item.groupKey === currentGroupRow?.groupKey)
      ) {
        currentPage.push(currentGroupRow)
      }

      currentPage.push(row)
      currentPageItemsCount += 1
    })

    if (currentPage.length) {
      result.push(currentPage)
    }

    return result
  }, [rows, pageSize])

  const visibleRows = pages[page] || []
  const totalPages = pages.length

  const renderCell = (item: T, column: IColumn<T>): ReactNode => {
    if (column.renderFunction) {
      return column.renderFunction(item, column)
    }

    return item[column.field as keyof T] as ReactNode
  }

  useEffect(() => {
    setPage(0)
  }, [rows])

  return (
    <div className={styles.root}>
      <table className={styles.table}>
        <colgroup>
          {columns.map((column, index) => (
            <col
              key={`${String(column.field)}-${index}`}
              style={column.width ? { width: column.width } : undefined}
            />
          ))}
        </colgroup>
        <thead className={styles.thead}>
          <tr>
            {columns.map((column) => (
              <th key={String(column.field)} className={column.className}>
                {column.titleRender ? column.titleRender({ column, headerLine: 1 }) : column.title}
              </th>
            ))}

            {rowActions.length > 0 && <th className={styles.actionsHeader} />}
          </tr>
        </thead>

        <tbody>
          {visibleRows.map((item, index) => (
            <tr
              key={`${item.isGroupRow ? 'group' : 'row'}-${item.groupKey || item.EntryId}-${index}`}
              className={cn(item.isGroupRow ? styles.groupTr : styles.rowTr, {
                [styles.copiedRow]:
                  !item.isGroupRow &&
                  item.EntryId != null &&
                  highlightedEntryIds?.has(item.EntryId),
              })}
            >
              {item.isGroupRow && columns.length > 0 ? (
                <>
                  <td
                    colSpan={Math.min(GROUP_ROW_NAME_COLUMN_SPAN, columns.length)}
                    className={cn(styles.groupCell, columns[0].className)}
                  >
                    {renderCell(item, columns[0])}
                  </td>
                  {columns.slice(GROUP_ROW_NAME_COLUMN_SPAN).map((column) => (
                    <td
                      key={String(column.field)}
                      className={cn(styles.groupCell, column.className)}
                    >
                      {renderCell(item, column)}
                    </td>
                  ))}
                </>
              ) : (
                columns.map((column) => (
                  <td key={String(column.field)} className={cn(styles.cell, column.className)}>
                    {renderCell(item, column)}
                  </td>
                ))
              )}

              {rowActions.length > 0 && (
                <td
                  className={
                    item.isGroupRow
                      ? `${styles.actionsCell} ${styles.groupCell}`
                      : styles.actionsCell
                  }
                >
                  {!item.isGroupRow && rowActionsVisible === 'visible' && (
                    <div className={styles.actions}>
                      {rowActions.map((rowAction) => {
                        const iconClassName =
                          typeof rowAction.icon === 'function'
                            ? rowAction.icon(item)
                            : rowAction.icon

                        return (
                          <Button
                            title={rowAction.title}
                            key={rowAction.id}
                            iconButton={iconClassName}
                            variant={'text'}
                            onClick={() => rowAction.action(item)}
                          />
                        )
                      })}
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button type="button" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Prev
          </button>

          <span>
            {page + 1} / {totalPages}
          </span>

          <button type="button" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  )
}
